import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { fetchPlatformStats, subscribeToAnalytics, type PlatformStat } from '../services/analytics';
import {
  TrendingUp, TrendingDown, Eye, Users, MousePointerClick,
  DollarSign, BarChart3, FileText, Share2, Search,
  Calendar, ChevronDown, ArrowUpRight, ArrowDownRight,
  Layers, Target, Zap, Globe
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';

// ── Mock data ─────────────────────────────────────────────────

const LIFECYCLE_DATA = [
  { week: 'W1', discovered: 12, generated: 8, approved: 6, published: 4, leads: 2 },
  { week: 'W2', discovered: 18, generated: 14, approved: 11, published: 9, leads: 5 },
  { week: 'W3', discovered: 22, generated: 17, approved: 15, published: 12, leads: 8 },
  { week: 'W4', discovered: 28, generated: 21, approved: 18, published: 16, leads: 12 },
  { week: 'W5', discovered: 35, generated: 28, approved: 24, published: 21, leads: 16 },
  { week: 'W6', discovered: 41, generated: 33, approved: 29, published: 26, leads: 20 },
  { week: 'W7', discovered: 38, generated: 30, approved: 27, published: 24, leads: 18 },
  { week: 'W8', discovered: 45, generated: 38, approved: 34, published: 31, leads: 24 },
];

const SEO_DATA = [
  { month: 'Nov', visibility: 42, clicks: 1200, impressions: 28000 },
  { month: 'Dec', visibility: 48, clicks: 1450, impressions: 31000 },
  { month: 'Jan', visibility: 55, clicks: 1800, impressions: 36000 },
  { month: 'Feb', visibility: 61, clicks: 2100, impressions: 40500 },
  { month: 'Mar', visibility: 68, clicks: 2500, impressions: 44800 },
  { month: 'Apr', visibility: 74, clicks: 2900, impressions: 49200 },
  { month: 'May', visibility: 79, clicks: 3300, impressions: 53600 },
];

const PLATFORM_DATA = [
  { platform: 'LinkedIn', posts: 34, reach: 128400, engagement: 4.8, leads: 42, roi: 312 },
  { platform: 'Blog/SEO', posts: 22, reach: 94200,  engagement: 3.2, leads: 68, roi: 480 },
  { platform: 'X/Twitter', posts: 48, reach: 76300, engagement: 2.1, leads: 18, roi: 142 },
  { platform: 'Instagram', posts: 29, reach: 112000, engagement: 6.4, leads: 31, roi: 226 },
  { platform: 'Meta Ads',  posts: 12, reach: 58000,  engagement: 1.9, leads: 22, roi: 178 },
];

const CONTENT_ROI = [
  { type: 'Blog Post',    count: 22, avgCost: 12, avgLeads: 3.1, roiMultiple: 8.4 },
  { type: 'LinkedIn Post',count: 34, avgCost: 4,  avgLeads: 1.2, roiMultiple: 11.2 },
  { type: 'Infographic',  count: 18, avgCost: 8,  avgLeads: 2.4, roiMultiple: 9.6 },
  { type: 'Carousel',     count: 14, avgCost: 6,  avgLeads: 1.8, roiMultiple: 10.8 },
  { type: 'X Thread',     count: 28, avgCost: 2,  avgLeads: 0.6, roiMultiple: 14.2 },
];

const TOP_CONTENT = [
  { title: 'Decentralised Trials: What 2026 Means for Sponsors', type: 'blog', views: 8420, leads: 28, seo: 91 },
  { title: 'GLP-1 Compound Controversy — Pharmacist Briefing',   type: 'carousel', views: 6180, leads: 22, seo: 87 },
  { title: 'AI Guest Personalisation: Hyatt & Marriott Case Study', type: 'blog', views: 5340, leads: 18, seo: 83 },
  { title: 'Hotel Tech Stack 2026 — Full Twitter Thread',          type: 'thread', views: 12600, leads: 14, seo: 72 },
  { title: 'RWE: 3 Regulatory Shifts Reshaping Drug Dev',          type: 'infographic', views: 4890, leads: 31, seo: 88 },
];

const RANGES = ['Last 30 days', 'Last 90 days', 'Last 6 months', 'This year'] as const;

function KpiCard({
  label, value, delta, deltaLabel, icon: Icon, color, suffix = ''
}: {
  label: string; value: string; delta: number; deltaLabel: string;
  icon: React.ComponentType<{ className?: string }>; color: string; suffix?: string;
}) {
  const up = delta >= 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-4 hover:border-slate-700/60 transition-colors"
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-xl ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className={`flex items-center gap-1 text-[11px] font-semibold ${up ? 'text-emerald-400' : 'text-red-400'}`}>
          {up ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
          {Math.abs(delta)}%
        </div>
      </div>
      <p className="text-2xl font-bold text-slate-100">{value}{suffix}</p>
      <p className="text-[11px] text-slate-500 mt-0.5">{label}</p>
      <p className="text-[10px] text-slate-600 mt-1">{deltaLabel}</p>
    </motion.div>
  );
}

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{color: string; name: string; value: number}>; label?: string }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700/60 rounded-xl px-3 py-2.5 shadow-xl">
      <p className="text-xs text-slate-400 mb-1.5">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="text-xs font-semibold" style={{ color: p.color }}>
          {p.name}: {p.value.toLocaleString()}
        </p>
      ))}
    </div>
  );
};

const Analytics: React.FC = () => {
  const [range, setRange] = useState<typeof RANGES[number]>('Last 90 days');
  const [showRangeMenu, setShowRangeMenu] = useState(false);

  // ── Live Supabase analytics state ──────────────────────────────────────────
  const [liveStats, setLiveStats]       = useState<PlatformStat[]>([]);
  const [liveLoading, setLiveLoading]   = useState(true);
  const [liveConnected, setLiveConnected] = useState(false);
  const unsubRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Initial fetch
    fetchPlatformStats()
      .then(stats => {
        setLiveStats(stats);
        setLiveLoading(false);
        if (stats.length > 0) setLiveConnected(true);
      })
      .catch(() => setLiveLoading(false));

    // Real-time subscription
    const unsub = subscribeToAnalytics(stats => {
      setLiveStats(stats);
      setLiveConnected(true);
    });
    unsubRef.current = unsub;

    return () => {
      unsubRef.current?.();
    };
  }, []);

  return (
    <Layout>
      <div className="px-6 py-6 space-y-6 max-w-[1400px] mx-auto">

        {/* ── Page header ───────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-slate-100">Analytics & ROI</h1>
            <p className="text-sm text-slate-500 mt-0.5">Content lifecycle tracking · SEO visibility · Revenue attribution</p>
          </div>
          <div className="relative">
            <button
              onClick={() => setShowRangeMenu(!showRangeMenu)}
              className="flex items-center gap-2 px-3 py-2 bg-slate-900/60 border border-slate-700/50 hover:border-slate-600/50 rounded-xl text-sm text-slate-300 transition-colors"
            >
              <Calendar className="w-3.5 h-3.5 text-slate-500" />
              {range}
              <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${showRangeMenu ? 'rotate-180' : ''}`} />
            </button>
            {showRangeMenu && (
              <div className="absolute right-0 top-full mt-1 bg-slate-900 border border-slate-700/60 rounded-xl overflow-hidden shadow-xl z-20 min-w-[160px]">
                {RANGES.map(r => (
                  <button
                    key={r}
                    onClick={() => { setRange(r); setShowRangeMenu(false); }}
                    className={`w-full px-3 py-2.5 text-left text-sm transition-colors ${r === range ? 'bg-violet-500/15 text-violet-300' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── KPI Grid ──────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard label="Leads Generated"  value="181"     delta={+34} deltaLabel="vs previous period" icon={Users}           color="text-violet-400 bg-violet-500/10" />
          <KpiCard label="SEO Visibility"   value="79"      delta={+22} deltaLabel="Visibility score"   icon={Search}          color="text-cyan-400 bg-cyan-500/10"   suffix="%" />
          <KpiCard label="Organic Reach"    value="475K"    delta={+18} deltaLabel="Total impressions"  icon={Globe}           color="text-emerald-400 bg-emerald-500/10" />
          <KpiCard label="Content ROI"      value="9.8×"    delta={+12} deltaLabel="Avg cost multiplier" icon={DollarSign}     color="text-amber-400 bg-amber-500/10" />
        </div>

        {/* ── Live Platform Stats (Real-time Supabase) ──────────── */}
        <div className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <MousePointerClick className="w-4 h-4 text-emerald-400" />
            <h2 className="text-sm font-semibold text-slate-200">Live Engagement Events</h2>
            <span className={`ml-auto flex items-center gap-1.5 text-[11px] font-semibold ${
              liveConnected ? 'text-emerald-400' : 'text-slate-500'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                liveConnected ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600'
              }`} />
              {liveConnected ? 'Live · Supabase Realtime' : 'Connecting…'}
            </span>
          </div>
          {liveLoading ? (
            <div className="flex items-center justify-center h-24 text-slate-500 text-sm">Loading live data…</div>
          ) : liveStats.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-24 gap-2">
              <Eye className="w-8 h-8 text-slate-700" />
              <p className="text-xs text-slate-500 text-center">
                No events yet — publish content to start tracking clicks, views & shares.
                <br />
                <span className="text-slate-600">(Events populate automatically via Supabase real-time)</span>
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-800/60">
                    {['Platform','Clicks','Views','Shares','Publishes','Errors','Total'].map(h => (
                      <th key={h} className="pb-2 pr-4 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {liveStats.map(row => (
                    <tr key={row.platform} className="border-b border-slate-800/30 hover:bg-slate-800/20">
                      <td className="py-2 pr-4 font-semibold text-slate-200">{row.platform}</td>
                      <td className="py-2 pr-4 text-cyan-400">{row.clicks.toLocaleString()}</td>
                      <td className="py-2 pr-4 text-violet-400">{row.views.toLocaleString()}</td>
                      <td className="py-2 pr-4 text-emerald-400">{row.shares.toLocaleString()}</td>
                      <td className="py-2 pr-4 text-amber-400">{row.success.toLocaleString()}</td>
                      <td className="py-2 pr-4 text-red-400">{row.errors.toLocaleString()}</td>
                      <td className="py-2 pr-4 text-slate-300 font-semibold">{row.total.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Content Lifecycle Funnel ──────────────────────────── */}
        <div className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-5">
            <Layers className="w-4 h-4 text-violet-400" />
            <h2 className="text-sm font-semibold text-slate-200">Content Lifecycle Tracking</h2>
            <span className="ml-auto text-[11px] text-slate-500">Discover → Generate → Approve → Publish → Leads</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={LIFECYCLE_DATA} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <defs>
                {[
                  { id: 'discovered', color: '#7c3aed' },
                  { id: 'generated',  color: '#0891b2' },
                  { id: 'approved',   color: '#059669' },
                  { id: 'published',  color: '#d97706' },
                  { id: 'leads',      color: '#db2777' },
                ].map(g => (
                  <linearGradient key={g.id} id={`lg-${g.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={g.color} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={g.color} stopOpacity={0.02} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="week" stroke="#475569" tick={{ fontSize: 10 }} />
              <YAxis stroke="#475569" tick={{ fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              {[
                { key: 'discovered', color: '#7c3aed', name: 'Discovered' },
                { key: 'generated',  color: '#0891b2', name: 'Generated' },
                { key: 'approved',   color: '#059669', name: 'Approved' },
                { key: 'published',  color: '#d97706', name: 'Published' },
                { key: 'leads',      color: '#db2777', name: 'Leads' },
              ].map(s => (
                <Area
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.name}
                  stroke={s.color}
                  strokeWidth={1.5}
                  fill={`url(#lg-${s.key})`}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-3">
            {[
              { label: 'Discovered', color: '#7c3aed' },
              { label: 'Generated',  color: '#0891b2' },
              { label: 'Approved',   color: '#059669' },
              { label: 'Published',  color: '#d97706' },
              { label: 'Leads',      color: '#db2777' },
            ].map(l => (
              <div key={l.label} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: l.color }} />
                <span className="text-[10px] text-slate-500">{l.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── SEO + Platform grid ───────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* SEO Visibility */}
          <div className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-5">
              <Search className="w-4 h-4 text-cyan-400" />
              <h2 className="text-sm font-semibold text-slate-200">SEO Visibility Trend</h2>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={SEO_DATA} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="month" stroke="#475569" tick={{ fontSize: 10 }} />
                <YAxis stroke="#475569" tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="visibility" name="Visibility Score" stroke="#22d3ee" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="clicks"     name="Organic Clicks"  stroke="#a78bfa" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Platform breakdown */}
          <div className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Share2 className="w-4 h-4 text-violet-400" />
              <h2 className="text-sm font-semibold text-slate-200">Platform Performance</h2>
            </div>
            <div className="space-y-3">
              {PLATFORM_DATA.map((p, idx) => (
                <div key={p.platform} className="flex items-center gap-3">
                  <span className="text-[11px] text-slate-500 w-20 flex-shrink-0">{p.platform}</span>
                  <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(p.reach / 128400) * 100}%` }}
                      transition={{ delay: idx * 0.1, duration: 0.7 }}
                      className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-500"
                    />
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-[11px] text-slate-400 w-14 text-right">{(p.reach / 1000).toFixed(0)}K</span>
                    <span className="text-[11px] text-emerald-400 w-10 text-right">{p.leads}L</span>
                    <span className="text-[11px] font-bold text-violet-400 w-12 text-right">{p.roi}%</span>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-3 pt-1 border-t border-slate-800/60 text-[10px] text-slate-600">
                <span className="w-20">Platform</span>
                <div className="flex-1" />
                <span className="w-14 text-right">Reach</span>
                <span className="w-10 text-right">Leads</span>
                <span className="w-12 text-right">ROI</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Content ROI Dashboard ─────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* ROI by type */}
          <div className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-5">
              <DollarSign className="w-4 h-4 text-amber-400" />
              <h2 className="text-sm font-semibold text-slate-200">Content ROI by Type</h2>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={CONTENT_ROI} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="type" stroke="#475569" tick={{ fontSize: 9 }} />
                <YAxis stroke="#475569" tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="roiMultiple" name="ROI Multiple" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                <Bar dataKey="avgLeads"    name="Avg Leads"    fill="#22d3ee" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Top performing content */}
          <div className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-4 h-4 text-emerald-400" />
              <h2 className="text-sm font-semibold text-slate-200">Top Performing Content</h2>
            </div>
            <div className="space-y-3">
              {TOP_CONTENT.map((c, idx) => (
                <div key={c.title} className="flex items-start gap-3 p-2.5 rounded-xl bg-slate-800/30 hover:bg-slate-800/50 transition-colors">
                  <span className="text-[10px] font-bold text-slate-600 w-4 flex-shrink-0 mt-0.5">#{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] text-slate-300 font-medium leading-snug line-clamp-1">{c.title}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-[10px] text-slate-600 capitalize">{c.type}</span>
                      <span className="text-[10px] text-slate-500">{(c.views / 1000).toFixed(1)}K views</span>
                      <span className="text-[10px] text-emerald-400">{c.leads} leads</span>
                      <span className="text-[10px] text-cyan-400">SEO {c.seo}</span>
                    </div>
                  </div>
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-1" />
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default Analytics;
